import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { login, resetPassword } from '../services/auth';
import { toast } from 'react-toastify';
import logo from '../images/logo/logo_1.png'; // Updated to original colored logo

export default function LoginPage() {
    const { session, loading } = useSelector((state) => state.auth);

    const [mode, setMode] = useState('sign_in'); // sign_in | forgot_password
    const [email, setEmail] = useState(() => localStorage.getItem('rememberedEmail') || '');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(() => !!localStorage.getItem('rememberedEmail'));
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (loading) {
        return (
            <div className="flex bg-slate-900 justify-center h-screen items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
            </div>
        );
    }

    if (session) {
        return <Navigate to="/dashboard" replace />;
    }

    const handleLogin = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        const { error } = await login(email, password);

        if (error) {
            toast.error(error.message === 'Invalid login credentials' ? 'Correo o contraseña incorrectos. Revisa tus credenciales y comunícate con un administrador si perdiste acceso.' : error.message);
        } else {
            if (rememberMe) {
                localStorage.setItem('rememberedEmail', email);
            } else {
                localStorage.removeItem('rememberedEmail');
            }
            toast.success('¡Bienvenido de vuelta!');
            window.location.href = '/dashboard';
        }
        setIsSubmitting(false);
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (!email) return toast.error('Ingresa tu correo electrónico para enviarte el enlace.');
        setIsSubmitting(true);

        const { error } = await resetPassword(email, `${window.location.origin}/reset-password`);

        if (error) {
            toast.error(error.message);
        } else {
            toast.success('Te hemos enviado un enlace seguro a tu bandeja de correo.');
            setMode('sign_in');
            setPassword('');
        }
        setIsSubmitting(false);
    };

    return (
        <div className="flex min-h-screen bg-slate-50 dark:bg-boxdark-2 antialiased selection:bg-[#3245a4] selection:text-white">
            {/* Left side - Branding/Image Overlay (Hidden on Mobile) */}
            <div className="hidden lg:flex lg:w-1/2 relative bg-[#091024] overflow-hidden items-center justify-center">
                {/* Subtle background pattern or gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#3245a4] to-[#0A1930] opacity-90"></div>

                {/* Decorative Glowing Orbs */}
                <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-[#3245a4] rounded-full blur-[120px] opacity-60"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[30rem] h-[30rem] bg-[#D4AF37] rounded-full blur-[150px] opacity-20"></div>

                <div className="relative z-10 p-12 text-center max-w-lg">
                    <div className="mb-8 inline-flex items-center justify-center w-24 h-24 rounded-[30px] bg-white/10 backdrop-blur-md shadow-2xl border border-white/20">
                        <span className="material-symbols-outlined text-[48px] text-white">travel_explore</span>
                    </div>
                    <h1 className="text-4xl font-black text-white px-8 leading-tight mb-6">El Motor detrás de <br /><span className="text-[#D4AF37]">Ruta Mundial</span></h1>
                    <p className="text-blue-100/70 text-lg leading-relaxed">
                        Accede a tu plataforma central para gestionar clientes, reservar destinos asombrosos y dominar las ventas.
                    </p>
                </div>
            </div>

            {/* Right side - Form Container */}
            <div className="flex flex-col flex-1 items-center justify-center p-6 sm:p-12">
                <div className="w-full max-w-[420px] bg-white dark:bg-boxdark rounded-[24px] shadow-2xl shadow-[#3245a4]/5 dark:shadow-none border border-slate-100 dark:border-strokedark p-8 sm:p-12 transition-all duration-300 transform scale-100">

                    <div className="text-center mb-10">
                        <div className="mx-auto w-[180px] h-[50px] mb-8 bg-contain bg-center bg-no-repeat" style={{ backgroundImage: `url(${logo})` }}>
                            {/* Logo Placeholder just in case */}
                            {!logo && <span className="font-extrabold text-[24px] text-[#3245a4] dark:text-white">RUTA MUNDIAL</span>}
                        </div>
                        <h2 className="text-2xl font-extrabold text-slate-800 dark:text-white tracking-tight">
                            {mode === 'sign_in' ? 'Acceso Corporativo' : 'Recuperar Acceso'}
                        </h2>
                        <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mt-2">
                            {mode === 'sign_in' ? 'Ingresa con tu correo corporativo para continuar.' : 'Recibe un enlace seguro en tu bandeja de correo.'}
                        </p>
                    </div>

                    <form onSubmit={mode === 'sign_in' ? handleLogin : handleResetPassword} className="space-y-6">
                        <div>
                            <label className="block text-[13px] font-bold text-slate-700 dark:text-slate-300 mb-2 uppercase tracking-wide">
                                Correo Electrónico
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <span className="material-symbols-outlined text-slate-400 text-[20px]">mail</span>
                                </div>
                                <input
                                    type="email"
                                    required
                                    placeholder="asesor@rutamundial.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full rounded-xl border border-slate-200 dark:border-strokedark bg-slate-50 dark:bg-meta-4 py-3.5 pl-11 pr-4 text-sm text-slate-800 dark:text-white focus:ring-2 focus:ring-[#3245a4] focus:border-transparent transition-all outline-none font-medium placeholder:font-normal"
                                />
                            </div>
                        </div>

                        {mode === 'sign_in' && (
                            <>
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="block text-[13px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wide">
                                            Contraseña
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => setMode('forgot_password')}
                                            className="text-[12px] font-semibold text-[#3245a4] dark:text-[#4A87FF] hover:underline"
                                        >
                                            ¿Olvidaste tu clave?
                                        </button>
                                    </div>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                            <span className="material-symbols-outlined text-slate-400 text-[20px]">lock</span>
                                        </div>
                                        <input
                                            type="password"
                                            required
                                            placeholder="••••••••"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="w-full rounded-xl border border-slate-200 dark:border-strokedark bg-slate-50 dark:bg-meta-4 py-3.5 pl-11 pr-4 text-sm text-slate-800 dark:text-white focus:ring-2 focus:ring-[#3245a4] focus:border-transparent transition-all outline-none font-medium placeholder:translate-y-1"
                                        />
                                    </div>
                                </div>
                                <div className="flex items-center">
                                    <input
                                        id="remember-me"
                                        type="checkbox"
                                        checked={rememberMe}
                                        onChange={(e) => setRememberMe(e.target.checked)}
                                        className="h-4 w-4 rounded border-slate-300 text-[#3245a4] focus:ring-[#3245a4] dark:border-strokedark dark:bg-meta-4 dark:ring-offset-boxdark"
                                    />
                                    <label htmlFor="remember-me" className="ml-2 block text-sm text-slate-700 dark:text-slate-300">
                                        Recordar mi correo
                                    </label>
                                </div>
                            </>
                        )}

                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="relative w-full overflow-hidden rounded-xl bg-[#3245a4] hover:bg-[#0B357F] text-white py-3.5 mt-2 font-black text-[15px] shadow-lg shadow-[#3245a4]/30 focus:outline-none focus:ring-2 focus:ring-[#3245a4] focus:ring-offset-2 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed group"
                        >
                            {isSubmitting ? (
                                <span className="flex items-center justify-center gap-2">
                                    <span className="material-symbols-outlined animate-spin text-[20px]">data_usage</span>
                                    Procesando...
                                </span>
                            ) : (
                                <span className="flex items-center justify-center gap-2">
                                    {mode === 'sign_in' ? 'Ingresar a mi Cuenta' : 'Enviar Enlace'}
                                    <span className="material-symbols-outlined text-[18px] group-hover:translate-x-1 transition-transform">
                                        {mode === 'sign_in' ? 'arrow_forward' : 'mail'}
                                    </span>
                                </span>
                            )}
                        </button>

                        {mode === 'forgot_password' && (
                            <button
                                type="button"
                                disabled={isSubmitting}
                                onClick={() => setMode('sign_in')}
                                className="w-full mt-4 py-2 text-sm font-semibold text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
                            >
                                ← Volver a Inicio de Sesión
                            </button>
                        )}
                    </form>

                    <div className="mt-10 text-center">
                        <p className="text-[11px] font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                            Uso exclusivo corporativo
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}